import { formatDateTime } from "./formatDateTime";
import { translatePaymentFailure } from "./paymentFailure";

/**
 * Client-side invoice PDF. We render a styled HTML invoice off-screen and rasterize it with
 * html2canvas, then drop the image onto an A4 page via jsPDF. Rasterizing (rather than drawing
 * text with jsPDF's core fonts) is deliberate: the UI is Azerbaijani/Russian and jsPDF's built-in
 * Helvetica can't render ə/ğ/ş/Cyrillic — the browser can, so we capture what it paints.
 *
 * jspdf + html2canvas are imported dynamically so they stay out of the main bundle and only load
 * when a user actually downloads an invoice.
 */

const STATUS_COLORS = {
  SUCCEEDED: { bg: "#dcfce7", fg: "#15803d" },
  FAILED: { bg: "#fee2e2", fg: "#b91c1c" },
  PENDING: { bg: "#fef3c7", fg: "#b45309" },
  REFUNDED: { bg: "#e2e8f0", fg: "#475569" },
};

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

/** Rasterize the brand SVG logo to a PNG data URL (transparent bg). Resolves null on failure. */
function loadLogoDataUrl(size = 220) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = "/gredus-logo.svg";
  });
}

function waitForImages(root) {
  const imgs = Array.from(root.querySelectorAll("img"));
  return Promise.all(
    imgs.map((img) =>
      img.complete ? Promise.resolve() : new Promise((res) => { img.onload = img.onerror = res; })
    )
  );
}

function buildInvoiceHtml({ tx, tenantName, t, logo }) {
  const L = {
    title: t("pages.invoices.pdf.title"),
    billedTo: t("pages.invoices.pdf.billedTo"),
    invoiceNo: t("pages.invoices.pdf.invoiceNo"),
    date: t("pages.invoices.pdf.date"),
    description: t("pages.invoices.pdf.description"),
    amount: t("pages.invoices.pdf.amount"),
    total: t("pages.invoices.pdf.total"),
    status: t("pages.invoices.pdf.status"),
    paymentRef: t("pages.invoices.pdf.paymentRef"),
    thankYou: t("pages.invoices.pdf.thankYou"),
    generatedNote: t("pages.invoices.pdf.generatedNote"),
  };

  const amountNum = Number(tx.amount);
  const amountStr = `${Number.isFinite(amountNum) ? amountNum.toFixed(2) : tx.amount} ${tx.currency || ""}`.trim();
  const invoiceNo = "INV-" + String(tx.id).padStart(6, "0");
  const statusLabel = t(`pages.invoices.status.${tx.status}`, { defaultValue: tx.status });
  const purposeLabel = t(`pages.invoices.purpose.${tx.purpose}`, { defaultValue: tx.purpose });
  const itemName = tx.itemName || purposeLabel;
  const sc = STATUS_COLORS[tx.status] || STATUS_COLORS.REFUNDED;
  const failureRow =
    tx.status === "FAILED"
      ? `<div style="font-size:12px; color:#dc2626; margin-top:8px;">${esc(translatePaymentFailure(t, tx.failureMessage))}</div>`
      : "";

  return `
  <div style="width:794px; min-height:1123px; box-sizing:border-box; background:#ffffff; padding:56px 56px 40px; font-family:Arial,Helvetica,sans-serif; color:#0f172a; display:flex; flex-direction:column;">
    <div style="display:flex; justify-content:space-between; align-items:flex-start;">
      <div style="display:flex; align-items:center;">
        ${logo ? `<img src="${logo}" width="54" height="54" style="display:block; margin-right:14px;" alt="" />` : ""}
        <div>
          <div style="font-size:26px; font-weight:800; letter-spacing:-0.5px; color:#1e293b;">Gredus</div>
          <div style="font-size:12px; color:#64748b; margin-top:2px;">gredus.com</div>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:30px; font-weight:800; letter-spacing:1px; color:#2563eb;">${esc(L.title)}</div>
        <div style="font-size:13px; color:#475569; margin-top:10px;">${esc(L.invoiceNo)}: <b>${esc(invoiceNo)}</b></div>
        <div style="font-size:13px; color:#475569; margin-top:3px;">${esc(L.date)}: ${esc(formatDateTime(tx.createdAt))}</div>
      </div>
    </div>

    <div style="height:4px; background:#2563eb; border-radius:2px; margin:24px 0 28px;"></div>

    <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:30px;">
      <div>
        <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.6px; color:#94a3b8; margin-bottom:6px;">${esc(L.billedTo)}</div>
        <div style="font-size:16px; font-weight:700; color:#0f172a;">${esc(tenantName || "—")}</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.6px; color:#94a3b8; margin-bottom:8px;">${esc(L.status)}</div>
        <span style="display:inline-block; padding:6px 16px; border-radius:999px; font-size:13px; font-weight:700; background:${sc.bg}; color:${sc.fg};">${esc(statusLabel)}</span>
      </div>
    </div>

    <table style="width:100%; border-collapse:collapse; font-size:14px;">
      <thead>
        <tr>
          <th style="text-align:left; padding:12px 14px; background:#f1f5f9; color:#475569; font-size:12px; text-transform:uppercase; letter-spacing:0.5px;">${esc(L.description)}</th>
          <th style="text-align:right; padding:12px 14px; background:#f1f5f9; color:#475569; font-size:12px; text-transform:uppercase; letter-spacing:0.5px;">${esc(L.amount)}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding:16px 14px; border-bottom:1px solid #e2e8f0; vertical-align:top;">
            <div style="font-weight:600; color:#0f172a;">${esc(itemName)}</div>
            <div style="font-size:12px; color:#94a3b8; margin-top:3px;">${esc(purposeLabel)}</div>
            ${failureRow}
          </td>
          <td style="padding:16px 14px; border-bottom:1px solid #e2e8f0; text-align:right; font-weight:600; white-space:nowrap;">${esc(amountStr)}</td>
        </tr>
      </tbody>
    </table>

    <div style="display:flex; justify-content:flex-end; margin-top:18px;">
      <div style="display:flex; justify-content:space-between; align-items:center; min-width:280px; background:#2563eb; color:#ffffff; padding:14px 20px; border-radius:10px;">
        <span style="font-size:14px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px;">${esc(L.total)}</span>
        <span style="font-size:18px; font-weight:800;">${esc(amountStr)}</span>
      </div>
    </div>

    <div style="margin-top:26px; font-size:13px; color:#475569;">
      ${esc(L.paymentRef)}: <span style="font-family:'Courier New',monospace; color:#0f172a;">${esc(tx.gatewayReference || "—")}</span>
    </div>

    <div style="margin-top:auto; padding-top:30px; border-top:1px solid #e2e8f0; text-align:center;">
      <div style="font-size:15px; font-weight:700; color:#2563eb;">${esc(L.thankYou)}</div>
      <div style="font-size:11px; color:#94a3b8; margin-top:8px;">${esc(L.generatedNote)}</div>
      <div style="font-size:11px; color:#cbd5e1; margin-top:4px;">${esc(formatDateTime(Date.now()))} · gredus.com</div>
    </div>
  </div>`;
}

/**
 * Generate and trigger download of a single transaction's invoice PDF.
 * @param {{tx: object, tenantName?: string, t: Function}} args
 */
export async function downloadInvoicePdf({ tx, tenantName, t }) {
  const [{ jsPDF }, html2canvasMod, logo] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
    loadLogoDataUrl(),
  ]);
  const html2canvas = html2canvasMod.default;

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.width = "794px";
  container.style.background = "#ffffff";
  container.innerHTML = buildInvoiceHtml({ tx, tenantName, t, logo });
  document.body.appendChild(container);

  try {
    await waitForImages(container);
    const canvas = await html2canvas(container.firstElementChild, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    // Encode as JPEG (not PNG): the invoice is a full-page raster, and a lossless PNG of it runs to
    // several MB. JPEG at q0.9 over a mostly-white page looks the same but is ~20-40x smaller, and jsPDF
    // stores it natively (DCTDecode) instead of re-deflating a bitmap. `compress` shrinks the rest too.
    const imgData = canvas.toDataURL("image/jpeg", 0.9);

    const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    let renderW = pageW;
    let renderH = (canvas.height / canvas.width) * renderW;
    if (renderH > pageH) {
      renderH = pageH;
      renderW = (canvas.width / canvas.height) * renderH;
    }
    pdf.addImage(imgData, "JPEG", (pageW - renderW) / 2, 0, renderW, renderH);
    pdf.save(`gredus-invoice-${tx.id}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
